import {useRef} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import {useSuiContext} from '@s-ui/react-context'
import IconArrowUpSmall from '@adv-mt/mt-svg-icons/lib/ArrowUpSmall.js'
import IconMicroOutline from '@adv-mt/mt-svg-icons/lib/MicroOutline.js'

const SpeechRecognitionAPI =
  typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)

const RecordingIconButton = ({disabled, handleText, listening, onStart, onStop, stopRef}) => {
  const {i18n} = useSuiContext()

  const recognitionRef = useRef(null)
  const transcriptRef = useRef('')

  if (!SpeechRecognitionAPI) {
    return null
  }

  const cleanUpRecognition = () => {
    const recognition = recognitionRef.current

    if (recognition) {
      recognition.onresult = null
      recognition.onend = null
      recognition.onerror = null
      recognition.stop()
      recognitionRef.current = null
    }

    transcriptRef.current = ''

    if (stopRef) {
      stopRef.current = null
    }

    onStop()
  }

  const stopRecording = () => {
    cleanUpRecognition()
  }

  const startRecording = () => {
    if (recognitionRef.current) {
      return
    }

    const recognition = new SpeechRecognitionAPI()

    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = i18n.culture

    recognition.onresult = event => {
      const results = Array.from(event.results)

      const transcript = results.map(result => result[0].transcript).join('')

      transcriptRef.current = transcript
      handleText(transcript)

      const hasFinalResult = results.some(result => result.isFinal)

      if (hasFinalResult) {
        cleanUpRecognition()
      }
    }

    recognition.onend = () => {
      if (!recognitionRef.current) {
        return
      }

      recognitionRef.current = null
      transcriptRef.current = ''

      if (stopRef) {
        stopRef.current = null
      }

      onStop()
    }

    recognition.onerror = () => {
      cleanUpRecognition()
    }

    recognitionRef.current = recognition

    if (stopRef) {
      stopRef.current = stopRecording
    }

    recognition.start()
    onStart()
  }

  return (
    <AtomButton
      aria-label={listening ? i18n.t('NET.INPUT_STOP_RECORDING_MESSAGE') : i18n.t('NET.INPUT_RECORD_MESSAGE')}
      className="mt-NetInput-recordButton"
      color={atomButtonColors.PRIMARY}
      design={atomButtonDesigns.FLAT}
      disabled={disabled}
      leftIcon={
        listening ? <IconArrowUpSmall size={atomIconSizes.medium} /> : <IconMicroOutline size={atomIconSizes.medium} />
      }
      onClick={listening ? stopRecording : startRecording}
      shape={atomButtonShapes.CIRCULAR}
      size={atomButtonSizes.SMALL}
    />
  )
}

RecordingIconButton.displayName = 'RecordingIconButton'

RecordingIconButton.propTypes = {
  disabled: PropTypes.bool,
  handleText: PropTypes.func.isRequired,
  listening: PropTypes.bool,
  onStart: PropTypes.func.isRequired,
  onStop: PropTypes.func.isRequired,
  stopRef: PropTypes.object
}

export default RecordingIconButton
